import { getApiUrl } from '../utils/apiConfig';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { getAuthHeaders } from '../utils/userUtils';

export const REFERENCE_MATERIAL_TEXT_MAX_LENGTH = 100000;
// 파일 자료의 추출 본문 교정 편집 상한 (채팅 파일 컨텍스트 한도와 동일)
export const REFERENCE_MATERIAL_FILE_MAX_LENGTH = 200000;
export const REFERENCE_MATERIAL_TITLE_MAX_LENGTH = 60;

const request = async (currentUser, path, { timeoutMs = 20_000, ...options } = {}) => {
  if (!currentUser) {
    throw new Error('로그인이 필요해요.');
  }
  const headers = await getAuthHeaders(currentUser);
  const method = String(options.method || 'GET').toUpperCase();
  let response;
  try {
    response = await fetchWithRetry(getApiUrl(path), {
      ...options,
      headers: {
        ...headers,
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {}),
      },
    }, {
      // 생성/삭제는 재시도하면 중복 저장·이중 삭제가 되므로 GET/PATCH만 재시도한다.
      maxRetries: method === 'GET' || method === 'PATCH' ? 1 : 0,
      timeoutMs,
      retryOnTimeout: method === 'GET',
    });
  } catch (requestError) {
    const message = /[가-힣]/.test(requestError?.message || '')
      ? requestError.message
      : '네트워크 연결을 확인한 뒤 다시 시도해주세요.';
    const safeError = new Error(message);
    safeError.status = 0;
    throw safeError;
  }

  let payload = {};
  try {
    payload = await response.json();
  } catch (_error) {
    payload = {};
  }
  if (!response.ok) {
    const safeServerDetail = (
      typeof payload.detail === 'string'
      && payload.detail.length <= 200
      && /[가-힣]/.test(payload.detail)
    ) ? payload.detail : null;
    const messages = {
      400: safeServerDetail || '입력한 내용을 다시 확인해주세요.',
      401: '로그인 정보를 다시 확인해주세요.',
      403: safeServerDetail || '이 기능은 PRO에서 사용할 수 있어요.',
      404: safeServerDetail || '자료를 찾을 수 없어요.',
      409: safeServerDetail || '자료함이 가득 찼어요. 사용하지 않는 자료를 정리해주세요.',
      422: '입력한 내용을 다시 확인해주세요.',
    };
    const error = new Error(
      messages[response.status]
      || '자료를 처리하지 못했어요. 잠시 후 다시 시도해주세요.'
    );
    error.status = response.status;
    throw error;
  }
  return payload;
};

export const listReferenceMaterials = (currentUser) => (
  request(currentUser, '/api/user/reference-materials')
);

// 자료 하나를 본문 전문(content)과 함께 가져온다 — 목록 API 는 preview(앞 120자)만 실어 준다.
export const getReferenceMaterial = (currentUser, materialId) => (
  request(
    currentUser,
    `/api/user/reference-materials/${encodeURIComponent(materialId)}`
  )
);

export const createTextReferenceMaterial = (currentUser, { content, title }) => (
  request(currentUser, '/api/user/reference-materials/text', {
    method: 'POST',
    body: JSON.stringify({ content, ...(title ? { title } : {}) }),
  })
);

export const createReferenceMaterialFromFile = (currentUser, { sourceFileId, title }) => (
  request(currentUser, '/api/user/reference-materials/from-file', {
    method: 'POST',
    body: JSON.stringify({
      source_file_id: sourceFileId,
      ...(title ? { title } : {}),
    }),
  })
);

// AI 참고 동의를 막 켠 대화의 첨부를 자료함에 모은다 (제외 규칙·동의는 서버가 다시 확인).
// 서버는 파일을 하나씩 담는다(큰 파일은 몇 초씩). 많이 보낼 때는 timeoutMs 를 늘린다.
export const autoCollectReferenceMaterials = (currentUser, fileIds, category, { timeoutMs } = {}) => (
  request(currentUser, '/api/user/reference-materials/auto-collect', {
    method: 'POST',
    body: JSON.stringify({
      file_ids: (fileIds || []).slice(0, 20),
      ...(category ? { category: String(category).slice(0, 60) } : {}),
    }),
    ...(timeoutMs ? { timeoutMs } : {}),
  })
);

// 지난 채팅 첨부 가져오기: { status: 'unavailable'|'none'|'running'|'done'|'stopped', candidates, total, done, saved, ... }
export const getLibraryBackfill = (currentUser) => (
  request(currentUser, '/api/user/reference-materials/backfill')
);

export const startLibraryBackfill = (currentUser) => (
  request(currentUser, '/api/user/reference-materials/backfill', { method: 'POST' })
);

export const updateReferenceMaterial = (currentUser, materialId, { title, content }) => (
  request(
    currentUser,
    `/api/user/reference-materials/${encodeURIComponent(materialId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify({
        ...(title !== undefined ? { title } : {}),
        ...(content !== undefined ? { content } : {}),
      }),
    }
  )
);

export const deleteReferenceMaterial = (currentUser, materialId) => (
  request(
    currentUser,
    `/api/user/reference-materials/${encodeURIComponent(materialId)}`,
    { method: 'DELETE' }
  )
);
